import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";
import { getStoredData } from "../../json/fecthData";

const ApiDataContext = createContext();

const ApiDataProvider = ({ children }) => {
  const [apiData, setApiData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllApiData = useCallback(async () => {
    try {
      setLoading(true);
      const data = getStoredData();
      const allApis = data?.[1]?.apis || [];

      const fetchedData = {};

      await Promise.all(
        allApis.map(async (apiItem) => {
          try {
            const res = await axios.get(
              `https://hammontrackingbackend-3.onrender.com/${apiItem.api}`
            );
            // ✅ Only assign if valid response
            fetchedData[apiItem.api] = res?.data ?? null;
          } catch (err) {
            console.error(`Error fetching ${apiItem.api}:`, err);
            fetchedData[apiItem.api] = null;
          }
        })
      );

      setApiData((prev) => ({
        ...prev,
        ...fetchedData, // ✅ Don't reset everything unless needed
      }));
    } catch (err) {
      console.error("Error in fetchAllApiData:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllApiData(); // ✅ Initial fetch only
  }, [fetchAllApiData]);

  const updateApiData = async (api, method, data = null, id = null) => {
    try {
      let response;
      const url = id
        ? `https://hammontrackingbackend-3.onrender.com/${api}/${id}`
        : `https://hammontrackingbackend-3.onrender.com/${api}`;

      if (method === "POST") {
        response = await axios.post(url, data);
      } else if (method === "PUT") {
        response = await axios.put(url, data);
      } else if (method === "DELETE") {
        response = await axios.delete(url, { data });
      } else {
        response = await axios.get(url);
      }

      // ✅ Only update the specific key, not all
      setApiData((prevData) => ({
        ...prevData,
        [api]: response.data,
      }));

      return response.data;
    } catch (err) {
      console.error(`Error with ${method} on ${api}:`, err);
      setError(err);
      throw err;
    }
  };

  return (
    <ApiDataContext.Provider
      value={{
        apiData,
        loading,
        error,
        updateApiData,
        refetchAll: fetchAllApiData, // 🔄 manual refresh option
      }}
    >
      {children}
    </ApiDataContext.Provider>
  );
};

// ✅ Hook to safely use required API keys
export const useApiData = (apisToFetch = []) => {
  const { apiData, loading, error, updateApiData, refetchAll } = useContext(ApiDataContext);

  const filteredData = apisToFetch.length
    ? Object.fromEntries(
        apisToFetch.map((key) => [key, apiData?.[key] ?? null])
      )
    : apiData;

  return {
    apiData: filteredData,
    loading,
    error,
    updateApiData,
    refetchAll,
  };
};

export default ApiDataProvider;
